import { FC } from 'react'
import socialNetworks from 'static/socialNetworks'

import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import { useRouter } from 'next/router'

type Variant = 'full' | 'reduced' | 'base'

interface IProps {
  host: string
  variant?: Variant
  path?: string
  pageTitle?: string
  orgDetails?: boolean
}

const SCHEMA = 'https://schema.org'

const TELEGRAM_BINANY24 = 'https://t.me/binany_bangladesh'

const SUPPORT_LANGUAGES = [
  'en',
  'hi',
  'bn',
  'id',
  'ms',
  'my',
  'am',
  'pa',
  'si',
  'sw',
  'vi',
  'ha',
  'ar',
  'th',
  'pt',
  'es',
]

const AREA_SERVED = [
  'IN',
  'MY',
  'ID',
  'BD',
  'TH',
  'VN',
  'NG',
  'BR',
  'CL',
  'EG',
  'GH',
  'KE',
  'MX',
  'PH',
  'PK',
  'RW',
  'TZ',
  'UG',
  'ZA',
]

const WEEK_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const CONTACT_POINT = {
  '@type': 'ContactPoint',
  contactType: 'customer support',
  email: 'support@binany.com',
  availableLanguage: SUPPORT_LANGUAGES,
  hoursAvailable: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: WEEK_DAYS,
    opens: '00:00',
    closes: '23:59',
  },
}

const jsonLd = (data: object) => (
  <Head>
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  </Head>
)

const StructuredData: FC<IProps> = ({
  host,
  variant = 'full',
  path = '',
  pageTitle,
  orgDetails = false,
}) => {
  const { t } = useTranslation('seo')
  const { locale, defaultLocale } = useRouter()

  if (!host) {
    return null
  }

  const origin = `https://${host}`
  const orgId = `${origin}/#organization`
  const localePrefix = locale === defaultLocale ? '' : `/${locale}`
  const url = `${origin}${localePrefix}${path}`
  const is24 = host === process.env.NEXT_PUBLIC_BN_HOSTNAME
  const logo = `${origin}/images/logoWhite.svg`

  const orgBase = {
    '@type': 'FinancialService',
    '@id': orgId,
    name: 'Binany',
    url,
    logo,
  }

  if (variant === 'base') {
    return jsonLd({ '@context': SCHEMA, ...orgBase })
  }

  const sameAs = socialNetworks.map((sn) =>
    sn.name === 'telegram' && is24 ? TELEGRAM_BINANY24 : sn.href,
  )

  const orgReduced = {
    ...orgBase,
    legalName: 'Finpulse LLC',
    inLanguage: locale,
    image: logo,
    serviceType: t('serviceType'),
    description: t('schemaDescription'),
    sameAs,
  }

  if (variant === 'reduced') {
    const orgNode = orgDetails
      ? {
          ...orgReduced,
          foundingDate: '2018',
          contactPoint: CONTACT_POINT,
          areaServed: AREA_SERVED,
        }
      : orgReduced

    return jsonLd({
      '@context': SCHEMA,
      '@graph': [
        orgNode,
        {
          '@type': 'WebPage',
          '@id': `${url}#webpage`,
          url,
          name: pageTitle,
          inLanguage: locale,
          about: { '@id': orgId },
        },
      ],
    })
  }

  return jsonLd({
    '@context': SCHEMA,
    ...orgReduced,
    currenciesAccepted: 'USD',
    paymentAccepted: 'PayTm, Easypaisa, PhonePe, UPI, bKash, Nagad, JazzCash, M-Pesa',
    contactPoint: CONTACT_POINT,
    areaServed: AREA_SERVED,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Tradable assets',
      itemListElement: [
        { '@type': 'OfferCatalog', name: 'Foreign currencies' },
        { '@type': 'OfferCatalog', name: 'Stocks' },
        { '@type': 'OfferCatalog', name: 'Crypto-assets' },
      ],
    },
  })
}

export default StructuredData
